import { useEffect, useMemo, useState } from 'react';

import { Check } from 'Src/feature/check/models';
import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { UseCheckItemButtonsOptions, useCurrentCheckItemButtons } from 'Src/feature/check-item/useCurrentCheckItemButtons';
import { useDiscount } from 'Src/feature/discount/useDiscount';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { useCurrentMenuScreen } from 'Src/feature/menu/useCurrentMenuScreen';
import { useTerminalSettings } from 'Src/feature/terminal/useTerminalSettings';
import useRoute from 'Src/routes/useRoute';

const { Field, Provider: FormProvider } = getTypedFormComponents<FormModel>();

class FormModel {
  screenId: number;
  constructor(part?: Partial<FormModel>) {
    if (part) Object.assign(this, part);
  }
}

/**
 * Entry point for a current check
 * - combine react-router state with other state mechanisms for "current check"
 */
const useCheckRouter = () => {
  const { crumbs, navigate, route } = useRoute();
  const { checkId, checkItemId } = route.params.active;

  const currentCheck = useCurrentCheck();
  const currentMenuScreen = useCurrentMenuScreen();
  const checkHeader = useCheckHeader.query.collection();
  const discount = useDiscount.query.getAll();
  const check = currentCheck.query.data;
  const currentCheckItem = check && checkItemId ? check.items.find(({ id }) => checkItemId === id) : undefined;

  const defaultViewNavigateBack = useTerminalSettings.defaultView.navigate.back();

  const isCurrentCheckClosed = check?.status === 'Closed';
  // Note: "Voided" status includes cleared checks. Cleared checks are a subtype of voided
  // (check.info.isCleared distinguishes them). Both should be treated as voided for routing purposes.
  const isCurrentCheckVoided = ['Voided', 'MergeVoided'].includes(check?.status ?? '');
  const isCurrentCheckCleared = isCurrentCheckVoided && (check?.info.isCleared ?? false);
  const checkItemSelectMode = route.params.active.itemSelect as 'one' | 'many';
  let checkItemButtonsMode: UseCheckItemButtonsOptions['mode'] = 'readonly';
  if(route.is(crumbs.pos.check.payment.main) || isCurrentCheckClosed || isCurrentCheckVoided) checkItemButtonsMode = 'readonly';
  else if(checkItemSelectMode === 'one') checkItemButtonsMode = 'edit-one';
  else if (checkItemSelectMode === 'many') checkItemButtonsMode = 'edit-many';

  const checkItemButtons = useCurrentCheckItemButtons({ mode: checkItemButtonsMode });

  const menuScreenOptions = currentMenuScreen.options?.length
    ? currentMenuScreen.options.sort((a, b) => a.position - b.position).map((op) => ({ label: op.name, value: op.id }))
    : [{ value: currentMenuScreen.screen?.id, label: currentMenuScreen.screen?.name }];

  const form = useForm<FormModel>({
    defaultValues: {
      screenId: currentMenuScreen.options?.find((op) => op.position === 1)?.id ?? currentMenuScreen.screen?.id,
    },
  });

  const [nextCheckId, nextCheckIdSet] = useState(-1 as Check['id']);

  // ----------current check init---------
  // set up for all check routers
  // - handles loading check
  const checkRequests = useMemo(() => {
    const requestCheckId = {
      current: checkId ? parseInt(checkId, 10) : undefined,
      next: nextCheckId,
    };
    return ({
      check: {
        id: requestCheckId,
        processing: currentCheck.query.isLoading,
        run: () => {
          currentCheck.currentIdSet(requestCheckId.current as number);
          nextCheckIdSet(requestCheckId.current as number);
        },
      },
      initial: {
        processing: currentMenuScreen.query.isLoading || checkHeader.isLoading || discount.isLoading,
        run: async () => {
          currentMenuScreen.currentArgsSet([{ checkId: requestCheckId.current ?? -1, includePriceLevel: true }]);
        },
      },
      dispose: () => {
        nextCheckIdSet(-1);
        currentCheck.currentIdSet(-1);
      },
    });
  }, [checkId, nextCheckId, currentCheck, currentMenuScreen, checkHeader.isLoading, discount.isLoading]);

  // get check data for the check views
  useEffect(() => {
    if (!checkRequests.check.id.current) return;
    if (checkRequests.check.processing) return;
    if (checkRequests.check.id.current === checkRequests.check.id.next) return;
    checkRequests.check.run();
  }, [checkRequests.check.id.next, checkRequests.check]);

  // navigation away from CheckRouter
  // - initial/dependent data for the check views
  // - remove stale state
  useEffect(() => {
    void checkRequests.initial.run();
    return () => checkRequests.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The following code is to reset the menu drop down to a valid selection. This is needed because on the first load from cold boot,
  // the menu screen store has NO data, so the form default values are "undefined". But that is not a valid selection. So once we have
  // the data loaded, we need to reset the form.
  const screenStoreCurrentId = currentMenuScreen.screen?.id;
  useEffect(() => {
    if (form.action.getValues('screenId') === undefined && screenStoreCurrentId !== undefined) form.action.reset({ screenId: screenStoreCurrentId });
  }, [form.action, screenStoreCurrentId]);
  // Related to the above, we need to reset the menu screen store to have no selection when we navigate away so the next time we come to the menu screen
  // we will NOT show a stale menu selection. We always load the default menu when we come to the menu screen.
  useEffect(() => () =>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    currentMenuScreen.dispose(), []);

  // ----------route logic---------
  const sharedRouteCrumbs = {
    ['check.main']: crumbs.pos.check.main,
    ['check.detail']: crumbs.pos.check.detail,
    ['check.item.edit']: crumbs.pos.check.item.edit,
    ['check.item.new']: crumbs.pos.check.item.new,
    ['check.payment.main']: crumbs.pos.check.payment.main,
  };

  // TODO: resolve complex components that can't easily be shared
  const separateRouteCrumbs = {
    ['check.transfer.main']: crumbs.pos.check.transfer.main,
    // current architecture is going to the same component when "check.splitAndTransfer.main" & "check.payment.splitTender"
    ['check.split.main']: crumbs.pos.check.split.main,
  };

  const routes = {
    shared: {
      ...sharedRouteCrumbs,
      isOne: Object.keys(sharedRouteCrumbs).reduce((accum, key) => {
        const routeCrumbKey = key as keyof typeof sharedRouteCrumbs;
        const routeCrumb = sharedRouteCrumbs[routeCrumbKey];
        accum[routeCrumbKey] = route.is(routeCrumb);
        return accum;
      }, {} as Record<keyof typeof sharedRouteCrumbs, boolean>),
      onGoBack: () => {
        // remove selections from check item buttons
        checkItemButtons.selectedCheckItemIdsSet([]);

        if (routes.shared.isOne['check.main'] && checkItemSelectMode === 'one') {
          // when at the last most check route we must go back to either the "check list" or the "floor plan" based on "terminalSettings.defaultView"
          defaultViewNavigateBack();
        } else {
          navigate(crumbs.pos.check.main.interpolate({
            checkId: currentCheck.query.data?.id,
            itemSelect: route.params.value.one,
          }));
        }
      }
    },
    separate: {
      ...separateRouteCrumbs,
      isOne: Object.keys(separateRouteCrumbs).reduce((accum, key) => {
        const routeCrumbKey = key as keyof typeof separateRouteCrumbs;
        const routeCrumb = separateRouteCrumbs[routeCrumbKey];
        accum[routeCrumbKey] = route.is(routeCrumb);
        return accum;
      }, {} as Record<keyof typeof separateRouteCrumbs, boolean>),
      isAny: () => Object.keys(separateRouteCrumbs).some((routeKey) => {
        const routeCrumbKey = routeKey as keyof typeof separateRouteCrumbs;
        const routeCrumb = separateRouteCrumbs[routeCrumbKey];
        return route.is(routeCrumb);
      })
    },
  };

  const isEditOneCheckItemRoute =
    routes.shared.isOne['check.item.new'] ||
    routes.shared.isOne['check.item.edit'];

  const isOverlayRoute =
    routes.shared.isOne['check.item.new'] ||
    routes.shared.isOne['check.item.edit'];

  // only show during single select
  const showCheckItemActions = checkItemSelectMode === 'one' && isEditOneCheckItemRoute;
  // only show during single select
  const showCheckPanelFooter = checkItemSelectMode === 'one' && !isEditOneCheckItemRoute;
  // only show during single select
  const showCheckOverlay = checkItemSelectMode === 'one' && isOverlayRoute;

  return {
    menuScreenOptions,
    form,
    onAfterChange: (value: number) => {
      // the getOne will error out if there are no menu screen options from the DB
      if (currentMenuScreen.options?.length) {
        currentMenuScreen.currentArgsSet([{ checkId: currentCheck.currentId, screenId: value, includePriceLevel: true }]);
      }
    },
    checkItemButtons,
    currentCheckItem,
    currentCheck,
    isCurrentCheckClosed,
    isCurrentCheckVoided,
    isCurrentCheckCleared,
    routes,
    showCheckItemActions,
    showCheckPanelFooter,
    showCheckOverlay,
    currentMenuScreen,
  };
};

export { Field, FormProvider, useCheckRouter };
